// SWR hook for the newsroom-editorial /electionday state.
//
// Public-side: fetches /api/electionday-state?kind=editorial. The
// endpoint reads from Postgres and is edge-cached (s-maxage=10,
// stale-while-revalidate=30) so thousands of reader polls on counting
// night collapse into ~one DB read per 10 s per region. Client SWR
// refresh stays at 12 s so editors' commits surface within one cycle.
//
// Admin-side: pass `admin: true` to read via the authenticated
// endpoint with no caching, so editors always see their own writes.

import useSWR, { type SWRConfiguration } from 'swr';
import {
  emptyEditorialState,
  type ElectionDayEditorial,
} from '../data/electionday-editorial-types';

const PUBLIC_URL = '/api/electionday-state?kind=editorial';
const ADMIN_URL = '/api/admin/electionday-editorial';

const fetcher = async (url: string): Promise<ElectionDayEditorial> => {
  const res = await fetch(url, { cache: 'no-store', credentials: 'include' });
  if (res.status === 404) return emptyEditorialState();
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as ElectionDayEditorial;
};

export interface UseEditorialOptions {
  admin?: boolean;
  refreshInterval?: number;
}

export function useElectionDayEditorial(
  options: UseEditorialOptions = {},
): {
  data: ElectionDayEditorial;
  isLoading: boolean;
  error: unknown;
  mutate: () => Promise<unknown>;
} {
  const url = options.admin ? ADMIN_URL : PUBLIC_URL;
  const config: SWRConfiguration = {
    refreshInterval: options.refreshInterval ?? 12_000,
    revalidateOnFocus: true,
    errorRetryCount: 2,
  };
  const { data, isLoading, error, mutate } =
    useSWR<ElectionDayEditorial>(url, fetcher, config);
  return {
    data: data ?? emptyEditorialState(),
    isLoading,
    error,
    mutate,
  };
}
